import HelperMixin from '../../../Shared/HelperMixin.vue';
import ApexCharts from 'apexcharts';

export default {
    name: 'Statistic',
    mixins: [HelperMixin],

    async created() {
        this.loginDetails = await this.CheckLoginStatus();
        if (this.loginDetails) {
            if (!this.requireUserType([1, 2, 3])) return; else {
                this.loadStatisticsData();
            }
        } else {
            this.logout();
        }
        window.scrollTo(0, 0);
    },

    data() {
        return {
            loading: true,
            error: null,
            stats: null,
            loginDetails: null,
            charts: {
                revenueComparison: null,
                tripDistribution: null,
                driverPerformance: null,
                performanceRadial: null,
                customersChart: null,
                salesOverview: null,
                financialSettlements: null,
                subscriptionStatus: null
            },
            rtlChartDefaults: {
                chart: {
                    fontFamily: 'inherit',
                    foreColor: '#adb0bb',
                    defaultLocale: 'ar',
                    locales: [{
                        name: 'ar',
                        options: {
                            months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
                            shortMonths: ['ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون', 'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس'],
                            days: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
                            shortDays: ['أحد', 'اثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت']
                        }
                    }]
                },
                tooltip: { theme: 'dark', style: { fontSize: '12px' } },
                yaxis: { opposite: true },
                legend: { position: 'bottom' }
            },
            arabicMonths: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
        };
    },

    beforeDestroy() {
        Object.values(this.charts).forEach(chart => { if (chart) chart.destroy(); });
    },

    methods: {
        async loadStatisticsData() {
            this.loading = true;
            this.error = null;
            try {
                const response = await this.$http.GetDashboardStatistics();
                this.stats = response.data;
                this.$nextTick(() => { this.initCharts(); });
            } catch (e) {
                this.error = 'حدث خطأ في تحميل البيانات';
                console.error(e);
            } finally {
                this.loading = false;
            }
        },

        initCharts() {
            if (!this.stats) return;
            this.initRevenueComparisonChart();
            this.initTripDistributionChart();
            this.initDriverPerformanceChart();
            this.initPerformanceRadialChart();
            this.initCustomersChart();
            this.initSalesOverviewChart();
            this.initFinancialSettlementsChart();
            this.initSubscriptionStatusChart();
        },

        initRevenueComparisonChart() {
            const el = document.querySelector('#revenue-comparison');
            if (!el || !this.stats) return;

            const currentYear = new Date().getFullYear();
            const previousYear = currentYear - 1;
            const currentYearData = this.stats.currentYearRevenue || Array(12).fill(0);
            const previousYearData = this.stats.previousYearRevenue || Array(12).fill(0);

            const options = {
                chart: { type: 'line', height: 305, toolbar: { show: false }, ...this.rtlChartDefaults.chart },
                series: [{ name: currentYear.toString(), data: currentYearData }, { name: previousYear.toString(), data: previousYearData }],
                colors: ['#5D87FF', '#FA896B'],
                stroke: { width: 3, curve: 'smooth' },
                markers: { size: 0 },
                grid: { show: true, borderColor: 'rgba(0,0,0,0.05)', xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
                xaxis: { categories: this.arabicMonths, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' } } },
                yaxis: { opposite: true, labels: { formatter: (val) => val.toLocaleString('en-US'), style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' } } },
                legend: { position: 'bottom', horizontalAlign: 'center' },
                tooltip: { ...this.rtlChartDefaults.tooltip, y: { formatter: (val) => val.toLocaleString('en-US') + ' د.ل' } }
            };

            if (this.charts.revenueComparison) this.charts.revenueComparison.destroy();
            this.charts.revenueComparison = new ApexCharts(el, options);
            this.charts.revenueComparison.render();
        },

        initTripDistributionChart() {
            const el = document.querySelector('#trip-distribution');
            if (!el || !this.stats) return;

            const options = {
                chart: { type: 'donut', height: 250, ...this.rtlChartDefaults.chart },
                series: [this.stats.regularTripsCount || 0, this.stats.sharedTripsCount || 0, this.stats.schoolTripsCount || 0],
                labels: ['عادية', 'مشتركة', 'مدارس'],
                colors: ['#5D87FF', '#49BEFF', '#FFAE1F'],
                legend: { position: 'bottom', horizontalAlign: 'center' },
                plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'الإجمالي', formatter: () => { const total = (this.stats.regularTripsCount || 0) + (this.stats.sharedTripsCount || 0) + (this.stats.schoolTripsCount || 0); return total.toLocaleString('en-US'); } } } } } },
                dataLabels: { enabled: false },
                tooltip: { ...this.rtlChartDefaults.tooltip, y: { formatter: (val) => val.toLocaleString('en-US') + ' رحلة' } }
            };

            if (this.charts.tripDistribution) this.charts.tripDistribution.destroy();
            this.charts.tripDistribution = new ApexCharts(el, options);
            this.charts.tripDistribution.render();
        },

        initDriverPerformanceChart() {
            const el = document.querySelector('#driver-performance');
            if (!el || !this.stats?.topDrivers) return;

            const topDrivers = this.stats.topDrivers || [];
            const options = {
                chart: { type: 'bar', height: 350, toolbar: { show: false }, ...this.rtlChartDefaults.chart },
                series: [{ name: 'مكتملة', data: topDrivers.map(d => d.completedTrips || 0) }, { name: 'ملغاة', data: topDrivers.map(d => d.cancelledTrips || 0) }],
                xaxis: { categories: topDrivers.map(d => d.driverName || ''), axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px', colors: '#adb0bb', fontWeight: '400' }, rotate: -45, rotateAlways: true } },
                colors: ['#13DEB9', '#FA896B'],
                plotOptions: { bar: { horizontal: false, columnWidth: '50%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                legend: { position: 'bottom', horizontalAlign: 'center' },
                grid: { show: true, borderColor: 'rgba(0,0,0,0.05)', xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
                yaxis: { opposite: true, labels: { style: { fontSize: '13px', colors: '#adb0bb', fontWeight: '400' } } },
                tooltip: { ...this.rtlChartDefaults.tooltip, y: { formatter: (val) => val + ' رحلة' } }
            };

            if (this.charts.driverPerformance) this.charts.driverPerformance.destroy();
            this.charts.driverPerformance = new ApexCharts(el, options);
            this.charts.driverPerformance.render();
        },

        initPerformanceRadialChart() {
            const el = document.querySelector('#performance-radial');
            if (!el || !this.stats) return;

            const completionRate = this.stats.completionRateToday || 0;
            const totalTrips = this.stats.totalTripsToday || 0;

            const options = {
                chart: { type: 'radialBar', height: 200, ...this.rtlChartDefaults.chart },
                series: [completionRate],
                labels: ['نسبة الاكتمال'],
                colors: ['#5D87FF'],
                plotOptions: { radialBar: { hollow: { size: '60%' }, dataLabels: { name: { fontSize: '14px', offsetY: -10 }, value: { fontSize: '20px', fontWeight: 'bold', formatter: (val) => val.toFixed(0) + '%' }, total: { show: true, label: 'إجمالي الرحلات', formatter: () => totalTrips.toLocaleString('en-US') } } } }
            };

            if (this.charts.performanceRadial) this.charts.performanceRadial.destroy();
            this.charts.performanceRadial = new ApexCharts(el, options);
            this.charts.performanceRadial.render();
        },

        initCustomersChart() {
            const el = document.querySelector('#customers-chart');
            if (!el || !this.stats) return;

            const registrations = this.stats.last7DaysRegistrations || [];
            const data = registrations.map(d => d.count || 0);
            const categories = registrations.map(d => { const date = new Date(d.date); return date.toLocaleDateString('ar-LY', { day: 'numeric', month: 'short' }); });

            const options = {
                chart: { type: 'area', height: 200, toolbar: { show: false }, sparkline: { enabled: false }, ...this.rtlChartDefaults.chart },
                series: [{ name: 'تسجيلات جديدة', data: data }],
                stroke: { curve: 'smooth', width: 2 },
                fill: { type: 'gradient', gradient: { shadeIntensity: 0, inverseColors: false, opacityFrom: 0.3, opacityTo: 0.1, stops: [0, 100] } },
                colors: ['#5D87FF'],
                xaxis: { categories: categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px', colors: '#adb0bb' } } },
                yaxis: { opposite: true, labels: { style: { fontSize: '11px', colors: '#adb0bb' } } },
                grid: { show: true, borderColor: 'rgba(0,0,0,0.05)', xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
                tooltip: { ...this.rtlChartDefaults.tooltip, y: { formatter: (val) => val + ' مستخدم' } }
            };

            if (this.charts.customersChart) this.charts.customersChart.destroy();
            this.charts.customersChart = new ApexCharts(el, options);
            this.charts.customersChart.render();
        },

        initSalesOverviewChart() {
            const el = document.querySelector('#sales-overview');
            if (!el || !this.stats) return;

            const options = {
                chart: { type: 'radialBar', height: 300, ...this.rtlChartDefaults.chart },
                series: [this.stats.regularTripRevenuePercent || 0, this.stats.schoolTripRevenuePercent || 0, this.stats.subscriptionRevenuePercent || 0, this.stats.voucherRevenuePercent || 0],
                labels: ['الرحلات العادية', 'رحلات المدارس', 'الاشتراكات', 'بطاقات الشحن'],
                colors: ['#5D87FF', '#49BEFF', '#FFAE1F', '#13DEB9'],
                plotOptions: { radialBar: { hollow: { size: '30%' }, track: { background: '#f2f2f2' }, dataLabels: { name: { fontSize: '12px' }, value: { fontSize: '14px', formatter: (val) => val.toFixed(0) + '%' }, total: { show: true, label: 'الإجمالي', formatter: () => '100%' } } } },
                legend: { show: true, position: 'bottom', horizontalAlign: 'center' }
            };

            if (this.charts.salesOverview) this.charts.salesOverview.destroy();
            this.charts.salesOverview = new ApexCharts(el, options);
            this.charts.salesOverview.render();
        },

        initFinancialSettlementsChart() {
            const el = document.querySelector('#financial-settlements');
            if (!el || !this.stats) return;

            const revenueData = this.stats.monthlyRevenueTracker || Array(12).fill(0);
            const withdrawalsData = this.stats.monthlyWithdrawals || Array(12).fill(0);

            const options = {
                chart: { type: 'line', height: 300, toolbar: { show: false }, ...this.rtlChartDefaults.chart },
                series: [{ name: 'الإيرادات', data: revenueData }, { name: 'المسحوبات', data: withdrawalsData }],
                colors: ['#13DEB9', '#FA896B'],
                stroke: { width: 2, curve: 'smooth' },
                markers: { size: 0 },
                grid: { show: true, borderColor: 'rgba(0,0,0,0.05)', xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
                xaxis: { categories: this.arabicMonths, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: '11px', colors: '#adb0bb' } } },
                yaxis: { opposite: true, labels: { formatter: (val) => val.toLocaleString('en-US'), style: { fontSize: '11px', colors: '#adb0bb' } } },
                legend: { position: 'bottom', horizontalAlign: 'center' },
                tooltip: { ...this.rtlChartDefaults.tooltip, y: { formatter: (val) => val.toLocaleString('en-US') + ' د.ل' } }
            };

            if (this.charts.financialSettlements) this.charts.financialSettlements.destroy();
            this.charts.financialSettlements = new ApexCharts(el, options);
            this.charts.financialSettlements.render();
        },

        initSubscriptionStatusChart() {
            const el = document.querySelector('#subscription-status');
            if (!el || !this.stats?.subscriptionsByStatus) return;

            const status = this.stats.subscriptionsByStatus;
            const options = {
                chart: { type: 'donut', height: 200, ...this.rtlChartDefaults.chart },
                series: [status.active || 0, status.expired || 0, status.stopped || 0],
                labels: ['نشط', 'منتهي', 'متوقف'],
                colors: ['#13DEB9', '#FA896B', '#FFAE1F'],
                legend: { position: 'bottom', horizontalAlign: 'center' },
                plotOptions: { pie: { donut: { size: '70%', labels: { show: true, total: { show: true, label: 'الإجمالي', formatter: () => { const total = (status.active || 0) + (status.expired || 0) + (status.stopped || 0); return total.toLocaleString('en-US'); } } } } } },
                dataLabels: { enabled: false },
                tooltip: this.rtlChartDefaults.tooltip
            };

            if (this.charts.subscriptionStatus) this.charts.subscriptionStatus.destroy();
            this.charts.subscriptionStatus = new ApexCharts(el, options);
            this.charts.subscriptionStatus.render();
        },

        formatNumber(num) {
            if (num === null || num === undefined) return '0';
            return num.toLocaleString('en-US');
        },

        formatCurrency(num) {
            if (num === null || num === undefined) return '0 د.ل';
            return num.toLocaleString('en-US') + ' د.ل';
        },

        formatPercent(num) {
            if (num === null || num === undefined) return '0%';
            const sign = num >= 0 ? '+' : '';
            return sign + num.toFixed(1) + '%';
        }
    }
};
